'use client'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { isCoordinateInsidePolygon } from '@components/atoms/GoogleMapsLoader'
import GoogleMapsPlaceSuggestions from '@components/atoms/GoogleMapsPlaceSuggestions'
import LocateMeButton from '@components/atoms/LocateMeBtn'
import { SaveAddressType, getAddresses, userContext } from '@components/context/userContext'
import { Button } from '@components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@components/ui/dialog'
import { Input } from '@components/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { GoogleMap } from '@react-google-maps/api'
import { MapPin } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { FC, useCallback, useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { LatLng, getGeocode } from 'use-places-autocomplete'
import * as z from 'zod'
const initialCenter: LatLng = { lat: 12.93108, lng: 77.614092 }
const containerStyle = {
  width: '400px',
  height: '400px'
}
const formSchema = z.object({
  name: z
    .string({ required_error: 'please give a name for the address' })
    .min(2, { message: 'please enter a name for the address' }),
  mobilenumber: z
    .string({ required_error: 'Please enter a phone number ' })
    .min(10, { message: 'Must be a valid mobile number' })
    .max(10, { message: 'Must be a valid mobile number' }),
  address: z.string(),
  locality: z.string().optional(),
  makeDefaultAddress: z.boolean().default(false)
})

interface EditAddress extends z.infer<typeof formSchema> {
  latitude: string
  longitude: string
  addressId: string
}

interface EditAddressForm extends SaveAddressType {
  addressid: string
}

export const editAddress = async (address: EditAddressForm) => {
  try {
    const res = await fetch('/api/address/update', {
      method: 'POST',
      body: JSON.stringify(address)
    })

    if (!res.ok) {
      throw new Error('failed to update address')
    }
    const response = await res.json()
    return
  } catch (error) {
    throw new Error('failed to update address')
  }
}

const deleteAddress = async (userId: string, addressId: string) => {
  try {
    const res = await fetch('/api/address/delete', {
      method: 'POST',
      body: JSON.stringify({ userId: userId, addressId: addressId })
    })
    if (!res.ok) {
      throw new Error('failed to delete')
    }
  } catch (error) {
    throw new Error('failed to delete')
  }
}
const EditAddressDialogue: FC<EditAddress> = ({ ...prefilled }) => {
  const user = useContext(userContext)
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: prefilled.address,
      locality: '',
      makeDefaultAddress: prefilled.makeDefaultAddress,
      mobilenumber: prefilled.mobilenumber,
      name: prefilled.name
    }
  })
  const [mapRef, setMapRef] = useState<google.maps.Map | undefined>(undefined)
  const handleDragEnd = useCallback(async () => {
    if (mapRef) {
      const lat = mapRef.getCenter()?.lat()
      const lon = mapRef.getCenter()?.lng()
      // console.log(lat)
      if (lat && lon) {
        const adresses = await getGeocode({ location: { lat: lat, lng: lon } })
        // console.log(adresses)
        const isInside = isCoordinateInsidePolygon({ lat: lat, lng: lon })
        user.setLocationCoords({
          displayAdress: adresses[0].formatted_address,
          latitude: lat,
          longitude: lon,
          isInsideTheCity: isInside
        })

        if (isInside) {
          form.setValue('address', adresses[0].formatted_address)
        }
      }
    }
  }, [form, mapRef, user])

  const onSubmit = useCallback(async (values: z.infer<typeof formSchema>) => {
    if (session) {
      const addressObj: EditAddressForm = {
        userid: session.user.userId,
        emailid: session.user.emailId,
        state: 'Karnataka',
        city: 'Bangalore',
        latitude: String(user.currentLocationCoords!.latitude),
        longitude: String(user.currentLocationCoords!.longitude),
        address: values.address,
        makedefaultaddress: values.makeDefaultAddress,
        mobilenumber: values.mobilenumber,
        name: values.name,
        locality: values.locality,
        addressid: prefilled.addressId
      }
      await editAddress(addressObj).then(async () => {
        queryClient.invalidateQueries(['addresses'])
        const newAddresses = await getAddresses(session.user.userId)
        if (user.setAddresses) {
          user.setAddresses({ method: 'addresses', addresses: newAddresses.addresses })
        }
      })
    }
  }, [])

  const handleDelete = useCallback(async () => {
    if (session) {
      await deleteAddress(session.user.userId, prefilled.addressId).then(async () => {
        queryClient.invalidateQueries(['addresses'])
        const newAddresses = await getAddresses(session.user.userId)
        if (user.setAddresses) {
          user.setAddresses({ method: 'addresses', addresses: newAddresses.addresses })
        }
      })
    }
  }, [prefilled.addressId, queryClient, session, user])
  return (
    <Dialog>
      <div className="md:w-full flex justify-end">
        <DialogTrigger>
          <div className="md:w-full flex justify-end">
            <Button
              variant='outline'
              className="label_small md:title_medium text-primary-500 md:text-primary-900 border-neutral-500 md:border"
              onClick={() => {
                user.setLocationCoords({
                  displayAdress: prefilled.address,
                  latitude: parseFloat(prefilled.latitude),
                  longitude: parseFloat(prefilled.longitude),
                  isInsideTheCity: true
                })
              }}
            >
              Edit
            </Button>
          </div>
        </DialogTrigger>
        <DialogContent className="min-w-[300px] w-auto">
          <div className=" relative flex justify-center items-center">
            <GoogleMap
              center={
                user.currentLocationCoords
                  ? {
                      lat: user.currentLocationCoords.latitude,
                      lng: user.currentLocationCoords.longitude
                    }
                  : initialCenter
              }
              zoom={17}
              // mapContainerStyle={containerStyle}
              mapContainerClassName="mapContainer"
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                minZoom: 10,
                zoomControl: false
              }}
              onLoad={(map) => setMapRef(map)}
              onDragEnd={handleDragEnd}
            />
            <MapPin
              fill="#ea0065 "
              size={36}
              stroke="#f25c99"
              className=" absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel>name</FormLabel> */}
                    <FormControl>
                      <div className=" border rounded-sm flex flex-col ">
                        <Input
                          {...field}
                          className=" w-full"
                          placeholder={session?.user.firstName + "'s home, Dad's home"}
                        />
                      </div>
                    </FormControl>
                    {/* <FormDescription>This is your public display name.</FormDescription> */}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className=" text-center text-red-500">
                {!user.currentLocationCoords?.isInsideTheCity && 'sorrry we dont have service'}
              </div>
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel className="pl-2 pt-1">name</FormLabel> */}
                    <FormControl>
                      <div className=" border rounded-sm flex flex-col ">
                        <div className=" label_medium font-bold pl-3">Location</div>
                        <div className="relative flex justify-between pb-2">
                          <GoogleMapsPlaceSuggestions className=" w-full label_small pl-2" />
                          <LocateMeButton
                            onLocationChange={(location) => {
                              // console.log(location)
                              user.setLocationCoords(location)
                              field.onChange(JSON.stringify(location.displayAdress))
                            }}
                            className="bg-white w-1/4"
                          />
                        </div>
                      </div>
                    </FormControl>
                    {/* <FormDescription>This is your public display name.</FormDescription> */}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locality"
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel className="pl-2 pt-1">name</FormLabel> */}
                    <FormControl>
                      <div className=" border rounded-sm flex flex-col ">
                        <Input {...field} className=" w-full" placeholder="landmark" />
                      </div>
                    </FormControl>
                    {/* <FormDescription>This is your public display name.</FormDescription> */}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mobilenumber"
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel className="pl-2 pt-1">name</FormLabel> */}
                    <FormControl>
                      <div className=" border rounded-sm flex flex-col ">
                        <Input
                          {...field}
                          type="number"
                          className=" w-full"
                          placeholder="phone number"
                        />
                      </div>
                    </FormControl>
                    {/* <FormDescription>This is your public display name.</FormDescription> */}
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* <div className="rounded-sm  border ">
            <Input placeholder="Landmark" className="w-full" />
          </div> */}
              <DialogClose disabled={!form.formState.isValid} className="w-full">
                {user.currentLocationCoords?.isInsideTheCity ? (
                  <Button
                    className=" text-white w-full"
                    type="submit"
                    disabled={!user.currentLocationCoords?.isInsideTheCity}
                  >
                    SAVE
                  </Button>
                ) : (
                  <div className=" text-center">We don't serve here</div>
                )}{' '}
              </DialogClose>
            </form>
          </Form>
          <Dialog>
            <DialogTrigger>
              <Button variant={'destructive'} className=" w-full">
                Delete
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Are you absolutely sure?</DialogTitle>
                <DialogDescription>
                  This action cannot be undone. Delete this address?
                </DialogDescription>
              </DialogHeader>
              <DialogClose className=" flex justify-end gap-2">
                <Button variant={'outline'}>Cancel</Button>
                <Button onClick={handleDelete} variant={'destructive'}>
                  Delete
                </Button>
              </DialogClose>
            </DialogContent>
          </Dialog>
        </DialogContent>
      </div>
    </Dialog>
  )
}

export default EditAddressDialogue
